import React, { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useSearchParams } from 'react-router-dom';
import { Box, Flex } from '@indeed/ifl-components';
import { ErrorDialog } from '../../../components/ErrorDialog/ErrorDialog';
import { useInitialState } from '../../../lib/context/InitialStateContext';
import GettextFactory from '../../../util/GettextFactory';
import { StandalonePageHeader } from '../components/StandalonePageHeader/StandalonePageHeader';
import { useApplyForMeCopy } from '../hooks/useApplyForMeCopy';
import { PaginationControls, PastAnswersContent, type PastAnswerItem } from './PastAnswers';

export const PAST_ANSWERS_PAGE_SIZE = 10;

export interface PastAnswersPageConnection {
    edges: PastAnswerItem[];
    pageInfo: {
        hasNextPage: boolean;
        endCursor?: string | null;
    };
    totalCount: number;
}

interface PastAnswersPageContentProps {
    connection: PastAnswersPageConnection | undefined;
    usePastAnswers: boolean | undefined;
    onUsePastAnswersChange: (checked: boolean) => Promise<boolean>;
    toggleDisabled: boolean;
    headerTitle: string;
    toggleLabel: string;
    fetching: boolean;
    sourceError: boolean;
    onRetrySource: () => void;
    onFetchMore: (first: number, after: string) => Promise<unknown>;
}

/** Shared Past Answers page UI and pagination for the SQ and DQ data sources. */
export const PastAnswersPageContent: React.FC<PastAnswersPageContentProps> = ({
    connection,
    usePastAnswers,
    onUsePastAnswersChange,
    toggleDisabled,
    headerTitle,
    toggleLabel,
    fetching,
    sourceError,
    onRetrySource,
    onFetchMore,
}) => {
    const gt = GettextFactory();
    const { locale, returnUrl } = useInitialState();
    const copy = useApplyForMeCopy();
    const [searchParams] = useSearchParams();
    const [showErrorDialog, setShowErrorDialog] = useState(false);
    const [retryAction, setRetryAction] = useState<() => void>(() => () => {});
    const [currentPage, setCurrentPage] = useState(1);

    useEffect(() => {
        if (sourceError) {
            setRetryAction(() => onRetrySource);
            setShowErrorDialog(true);
        }
    }, [sourceError, onRetrySource]);

    const onPageChange = useCallback(
        (page: number) => {
            setCurrentPage(page);

            if (!connection) return;
            const itemsNeeded = page * PAST_ANSWERS_PAGE_SIZE;
            if (
                itemsNeeded > connection.edges.length &&
                connection.pageInfo.hasNextPage &&
                connection.pageInfo.endCursor
            ) {
                const fetchCount = itemsNeeded - connection.edges.length;
                onFetchMore(fetchCount, connection.pageInfo.endCursor).catch(() => {
                    setCurrentPage(currentPage);
                    setRetryAction(() => () => onPageChange(page));
                    setShowErrorDialog(true);
                });
            }
        },
        [connection, currentPage, onFetchMore],
    );

    const handleUsePastAnswersChange = async (checked: boolean): Promise<void> => {
        const succeeded = await onUsePastAnswersChange(checked);
        if (succeeded && !checked) setCurrentPage(1);
    };

    const startIndex = (currentPage - 1) * PAST_ANSWERS_PAGE_SIZE;
    const pageData =
        connection && (connection.edges.length === 0 || startIndex < connection.edges.length)
            ? connection.edges.slice(startIndex, startIndex + PAST_ANSWERS_PAGE_SIZE)
            : undefined;

    const handleClose = () => {
        if (searchParams.get('from') === 'auto-apply-setup') {
            window.close();
        } else {
            window.location.href = returnUrl;
        }
    };

    return (
        <>
            <Helmet>
                <title>{copy.pastAnswersTitle}</title>
                <meta
                    name="description"
                    content={gt.gettext('Manage your past answers for automatic applications')}
                />
            </Helmet>

            <Flex sx={{ inlineSize: '100%', justifyContent: 'center' }}>
                <Box sx={{ inlineSize: '100%', maxInlineSize: '25.125rem' }}>
                    <Box sx={{ paddingInline: 4, paddingBlock: 2 }}>
                        <StandalonePageHeader title={headerTitle} onClose={handleClose} />
                    </Box>

                    <Box sx={{ paddingInline: 4, paddingBlockEnd: 9 }}>
                        <PastAnswersContent
                            pastAnswers={pageData}
                            usePastAnswers={usePastAnswers}
                            onUsePastAnswersChange={handleUsePastAnswersChange}
                            toggleDisabled={toggleDisabled}
                            toggleLabel={toggleLabel}
                            locale={locale}
                        />
                    </Box>
                </Box>
            </Flex>

            {usePastAnswers && typeof connection?.totalCount === 'number' && (
                <Box
                    sx={{
                        position: 'fixed',
                        insetBlockEnd: 0,
                        insetInlineStart: '50%',
                        transform: 'translateX(-50%)',
                        inlineSize: '100%',
                        maxInlineSize: '25.125rem',
                        paddingBlock: 4,
                        backgroundColor: 'background',
                    }}
                >
                    <PaginationControls
                        currentPage={currentPage}
                        totalCount={connection.totalCount}
                        onPageChange={onPageChange}
                        itemsPerPage={PAST_ANSWERS_PAGE_SIZE}
                        disabled={fetching}
                    />
                </Box>
            )}

            <ErrorDialog
                isOpen={showErrorDialog}
                onOpenChange={setShowErrorDialog}
                onAction={retryAction}
            />
        </>
    );
};
